<template>
  <div>
    <!-- Search bar - always visible -->
    <div class="px-2 mt-3 mb-0">
      <v-text-field
        v-model="search"
        placeholder="Zoeken..."
        solo
        clearable
        @click:clear="clearSearch"
        @input="onSearchInput"
      >
        <template #append>
          <CounterBadgeIcon
            :counter="selected_options"
            icon="mdi-account-filter"
            @click="openFilterWindow"
          />
          <v-btn
            icon
            variant="text"
            title="Ververs lijst vanaf API"
            @click="syncWithStore"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Loading spinner - shown when loading initial accounts -->
    <div
      v-if="accountSearchStore.loading && accounts.length === 0"
      class="text-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
        width="4"
      ></v-progress-circular>
      <p class="text-body-1 text-medium-emphasis mt-4 mb-0">
        Accounts worden geladen...
      </p>
    </div>

    <!-- Main content area -->
    <div v-else>
      <AccountFilterDialog
        v-model:model-value="q"
        :show="show"
        @update:show="show = $event"
        @search="searchAccounts"
        @close_window="closeFilterDialog"
        @clear_search="clearSearchString"
        @selected_options="updateSelectedOptions"
      />

      <!-- Show no results message when search is active but no accounts found -->
      <div
        v-if="showNoResultsMessage"
        class="d-flex justify-center align-center pa-8"
      >
        <v-card
          class="text-center pa-8 ma-4"
          variant="outlined"
          color="surface"
          max-width="500"
          elevation="0"
        >
          <div class="no-results-container">
            <div class="icon-wrapper mb-6">
              <v-avatar size="80" color="grey-lighten-4" class="mb-4">
                <v-icon size="40" color="grey-lighten-1">
                  mdi-account-search-outline
                </v-icon>
              </v-avatar>
            </div>

            <div class="content-wrapper">
              <h3 class="text-h5 text-grey-darken-2 mb-3 font-weight-medium">
                Geen resultaten gevonden
              </h3>
              <p
                class="text-body-1 text-grey-darken-1 mb-6 mx-auto"
                style="max-width: 350px; line-height: 1.5"
              >
                Er zijn geen accounts gevonden die voldoen aan je zoekcriteria.
                Probeer je zoekopdracht aan te passen of wis de filters.
              </p>
            </div>

            <div class="actions-wrapper">
              <v-btn
                variant="elevated"
                color="primary"
                size="large"
                class="px-8"
                @click="clearSearch"
              >
                <v-icon start>mdi-filter-remove</v-icon>
                Zoekfilters wissen
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>

      <v-list v-else class="pa-0" lines="three" density="compact">
        <template
          v-for="(account, index) in accounts"
          :key="`account-${account.id}`"
        >
          <AccountListItem :account="account" />
          <v-divider v-if="index < accounts.length - 1" inset />
        </template>

        <V3InfiniteLoading :identifier="infiniteId" @infinite="infiniteHandler">
          <!-- Custom loading template -->
          <template #loading>
            <div class="text-center py-6">
              <v-progress-circular
                indeterminate
                color="primary"
                size="32"
                width="3"
              ></v-progress-circular>
              <p class="text-body-2 text-grey-darken-1 mt-3 mb-0">
                Accounts worden geladen...
              </p>
            </div>
          </template>

          <!-- Custom complete template (no more data) -->
          <template #complete>
            <div class="d-flex justify-center align-center py-8">
              <v-card
                class="text-center pa-6"
                variant="flat"
                color="grey-lighten-5"
                max-width="400"
                rounded="lg"
              >
                <div class="no-more-container">
                  <div class="icon-wrapper mb-4">
                    <v-avatar size="56" color="grey-lighten-3" class="mb-2">
                      <v-icon size="28" color="grey-darken-1">
                        mdi-check-circle-outline
                      </v-icon>
                    </v-avatar>
                  </div>

                  <div class="content-wrapper">
                    <h4
                      class="text-h6 text-grey-darken-2 mb-2 font-weight-medium"
                    >
                      Alle accounts geladen
                    </h4>
                    <p
                      class="text-body-2 text-grey-darken-1 mb-0"
                      style="line-height: 1.4"
                    >
                      Er zijn geen accounts meer om te laden.
                      <span v-if="accounts.length > 0">
                        {{ accounts.length }} account{{
                          accounts.length !== 1 ? "s" : ""
                        }}
                        gevonden.
                      </span>
                    </p>
                  </div>
                </div>
              </v-card>
            </div>
          </template>

          <!-- Custom error template -->
          <template #error>
            <div class="text-center py-6">
              <v-icon size="48" color="error" class="mb-3">
                mdi-alert-circle-outline
              </v-icon>
              <p class="text-body-1 text-error mb-3">
                Er is een fout opgetreden bij het laden
              </p>
              <v-btn
                color="error"
                variant="outlined"
                size="small"
                @click="refreshData"
              >
                <v-icon start>mdi-refresh</v-icon>
                Opnieuw proberen
              </v-btn>
            </div>
          </template>
        </V3InfiniteLoading>
      </v-list>
    </div>
  </div>
</template>

<script>
import AccountListItem from "./AccountListItem.vue";
import AccountFilterDialog from "./AccountFilterDialog.vue";
import CounterBadgeIcon from "../ui/CounterBadgeIcon.vue";

import { useAccountSearchStore } from "../../stores/accountSearchStore";
import { useContextMenuStore } from "../../stores/contextMenuStore";
import { useFormsStore } from "../../stores/formsStore";
import V3InfiniteLoading from "v3-infinite-loading";
import { onMounted, onUnmounted, ref, computed } from "vue";
import { useAccountCategoriesStoreRefactor } from "../../stores/accountCategoriesStoreRefactor";
import { useAccountLeadsStoreRefactor } from "../../stores/accountLeadsStoreRefactor";

// Constants
const DEFAULT_YEAR_RANGE = [1922, 2025];
const DEBOUNCE_DELAY = 700;

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Factory function for creating default search parameters
const createDefaultSearchParams = () => ({
  first_name_or_last_name_or_description_or_problem_or_actions_or_regional_binding_or_keno_code_i_cont_all:
    "",
  mz_eq: "",
  vip_eq: "",
  psychiatrisch_eq: "",
  gender_eq: "",
  legal_number_cont: "",
  repat_eq: "",
  country_eq: "",
  date_of_birth_age_eq: "",
  year_of_birth_between: [...DEFAULT_YEAR_RANGE],
  account_lead_id_eq: "",
  account_category_id_eq: "",
  selected_traits_id_search_cont_all: "",
  selected_traits_id_search_cont_any: "",
});

export default {
  components: {
    AccountListItem,
    AccountFilterDialog,
    CounterBadgeIcon,
    V3InfiniteLoading,
  },

  setup() {
    // Initialize stores - only need the search store
    const accountSearchStore = useAccountSearchStore();
    const contextMenuStore = useContextMenuStore();
    const formsStore = useFormsStore();

    // Component state
    const infiniteId = ref(0);
    const search = ref("");
    const show = ref(false);

    // Search parameters
    const q = ref(createDefaultSearchParams());

    // Computed properties - always use search store
    const accounts = computed(() => accountSearchStore.searchResults || []);

    // Compute active filter count from q object
    const selected_options = computed(() => {
      return Object.entries(q.value).reduce((count, [key, value]) => {
        // Skip text search and default year range
        if (
          key ===
          "first_name_or_last_name_or_description_or_problem_or_actions_or_regional_binding_i_cont_all"
        )
          return count;
        if (
          key === "year_of_birth_between" &&
          Array.isArray(value) &&
          value[0] === DEFAULT_YEAR_RANGE[0] &&
          value[1] === DEFAULT_YEAR_RANGE[1]
        )
          return count;

        // Count non-empty values
        if (Array.isArray(value)) return count + (value.length > 0 ? 1 : 0);
        return (
          count +
          (value !== null && value !== undefined && value !== "" ? 1 : 0)
        );
      }, 0);
    });

    // Compute if search is active (for UI feedback)
    const isSearchActive = computed(() => {
      return search.value.trim() !== "" || selected_options.value > 0;
    });

    // Determine when to show "no results" message
    const showNoResultsMessage = computed(() => {
      return (
        isSearchActive.value &&
        !accountSearchStore.hasMoreData &&
        accounts.value.length === 0 &&
        !accountSearchStore.loading
      );
    });

    // Helper functions
    const refreshData = () => {
      accountSearchStore.clear();
      infiniteId.value += 1;
    };

    // Prepare search parameters with common logic
    const prepareSearchParams = (includeTextSearch = true) => {
      // Set text search if requested
      if (includeTextSearch && search.value.trim()) {
        q.value.first_name_or_last_name_or_description_or_problem_or_actions_or_regional_binding_or_keno_code_i_cont_all =
          search.value.trim();
      } else {
        q.value.first_name_or_last_name_or_description_or_problem_or_actions_or_regional_binding_or_keno_code_i_cont_all =
          "";
      }

      // Clone search params
      let searchParams = { ...q.value };

      // Remove default year range if unchanged
      if (
        searchParams.year_of_birth_between[0] === DEFAULT_YEAR_RANGE[0] &&
        searchParams.year_of_birth_between[1] === DEFAULT_YEAR_RANGE[1]
      ) {
        delete searchParams.year_of_birth_between;
      }

      return searchParams;
    };

    // Execute search with given parameters
    const executeSearch = async (searchParams, append = false) => {
      return await accountSearchStore.search(searchParams, append);
    };

    // Unified search method
    const performSearch = async (includeTextSearch = true, append = false) => {
      // Don't automatically clear - let cached results show while fetching
      const searchParams = prepareSearchParams(includeTextSearch);
      return await executeSearch(searchParams, append);
    };

    // Debounced search when user types (no clear - shows cached while fetching)
    const debouncedSearch = debounce(() => {
      performSearch(search.value.trim() !== "");
    }, DEBOUNCE_DELAY);

    // Handle search input changes
    const onSearchInput = () => {
      debouncedSearch();
    };

    const syncWithStore = () => {
      refreshData(); // Explicit clear for refresh button
      const includeTextSearch = search.value.trim() !== "";
      performSearch(includeTextSearch);
    };

    const createNewAccount = () => {
      formsStore.edit({
        type: "account",
        item: {},
      });
    };

    const clearSearch = () => {
      // Reset ALL search parameters
      q.value = createDefaultSearchParams();
      search.value = "";

      // Load all accounts (empty search returns all)
      refreshData(); // Explicit clear for filter reset
      performSearch(false);
    };

    const updateSelectedOptions = () => {
      // This is now computed, but we keep the method for compatibility
      // The actual count is computed from q.value automatically
    };

    // Track if component is still mounted to prevent state updates after unmount
    let isMounted = true;

    const infiniteHandler = async function ($state) {
      try {
        // 🔒 CRITICAL: Check if component is still mounted
        if (!isMounted) {
          console.log("InfiniteHandler: Component unmounted, aborting");
          return;
        }

        // 🔒 CRITICAL FIX: Prevent infinite loading during ActionCable sync
        // This prevents V3InfiniteLoading from triggering when data changes due to real-time updates
        if (accountSearchStore.actionCableSyncInProgress) {
          console.log("InfiniteHandler: Skipping during ActionCable sync");
          return $state.loaded();
        }

        // Defensive check: if we already have data and know there's no more, complete immediately
        if (
          accounts.value.length > 0 &&
          accounts.value.length < 25 &&
          !accountSearchStore.hasMoreData
        ) {
          console.log(
            "InfiniteHandler: Already have all data, completing immediately",
          );
          return $state.complete();
        }

        if (accountSearchStore.loading) {
          return $state.loaded();
        }

        if (!accountSearchStore.hasMoreData) {
          return $state.complete();
        }

        const includeTextSearch = search.value.trim() !== "";
        const newData = await performSearch(includeTextSearch, true);

        // 🔒 Check mounted state again after async operation
        if (!isMounted) {
          console.log(
            "InfiniteHandler: Component unmounted after fetch, aborting state update",
          );
          return;
        }

        if (newData && newData.length > 0) {
          $state.loaded();
        } else {
          $state.complete();
        }
      } catch (error) {
        // Don't log errors if component was unmounted (expected during navigation)
        if (!isMounted) return;
        console.error("Error in infinite handler:", error);
        $state.error();
      }
    };

    // Define context menu options
    const context_menu = [
      {
        name: "DG toevoegen",
        action: createNewAccount,
        icon: "mdi-account-plus",
      },
      {
        name: "Lijst verversen",
        action: refreshData,
        icon: "mdi-refresh",
      },
    ];

    // Initialize component
    onMounted(async () => {
      contextMenuStore.addOption(context_menu);

      // Force reset of infinite loading component when mounting
      infiniteId.value += 1;

      // Initialize only the search store
      await accountSearchStore.init();

      try {
        // Start with empty search to load all accounts
        // If store has cached data, it will show while fetching fresh data
        await performSearch(false);
      } catch (error) {
        console.error("Error during initial accounts load:", error);
      }
    });

    // Cleanup on unmount
    onUnmounted(() => {
      // 🔒 CRITICAL: Mark component as unmounted to prevent async operations from updating state
      isMounted = false;

      contextMenuStore.reset();

      // 🔒 RACE CONDITION FIX: Cancel all pending requests FIRST to prevent stale responses
      console.log("AccountList: Cancelling all pending requests on unmount");
      if (accountSearchStore.cancelAllRequests) {
        accountSearchStore.cancelAllRequests();
      }

      // Clear search store state completely to ensure fresh start when navigating back
      console.log("AccountList: Clearing search store state on unmount");
      accountSearchStore.clear();
      accountSearchStore.hasMoreData = true; // Reset for next time

      // Get references to the stores used by AccountFilterDialog
      const accountCategoriesStore = useAccountCategoriesStoreRefactor();
      const accountLeadsStore = useAccountLeadsStoreRefactor();

      // Cancel their pending requests too
      if (accountCategoriesStore.cancelAllRequests) {
        accountCategoriesStore.cancelAllRequests();
      }

      if (accountLeadsStore.cancelAllRequests) {
        accountLeadsStore.cancelAllRequests();
      }
    });

    return {
      // Store - only expose search store
      accountSearchStore,

      // State
      infiniteId,
      search,
      show,
      q,

      // Computed
      accounts,
      selected_options,
      isSearchActive,
      showNoResultsMessage,

      // Methods
      refreshData,
      syncWithStore,
      createNewAccount,
      clearSearch,
      searchAccounts: () => performSearch(true), // Filter search - shows cached while fetching
      onSearchInput,
      openFilterWindow: () => (show.value = true), // Inline simple function
      closeFilterDialog: () => (show.value = false), // Inline simple function
      updateSelectedOptions,
      clearSearchString: clearSearch, // Alias for compatibility
      infiniteHandler,
    };
  },
};
</script>

<style scoped>
.no-results-container,
.no-more-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.icon-wrapper {
  flex-shrink: 0;
}

.content-wrapper {
  flex-grow: 1;
  text-align: center;
}

.actions-wrapper {
  flex-shrink: 0;
}
</style>
